import type {
  BenchmarkResult,
  DiagnosisFinding,
  DiagnosisReport,
  ScoreResult,
} from '../diagnosis/contracts';
import type { AIEvidenceReference, AIRequest } from './contracts';

export interface AIFunnelMetadata {
  readonly id: string;
  readonly name: string;
  readonly description?: string;
  readonly nodeCount: number;
  readonly edgeCount: number;
}

export interface AINodeSummary {
  readonly id: string;
  readonly type: string;
  readonly name: string;
  readonly status: string;
  readonly metricStates?: Readonly<Record<string, unknown>>;
}

export interface AIEdgeSummary {
  readonly id: string;
  readonly source: string;
  readonly target: string;
  readonly type: string;
  readonly conversion?: unknown;
}

export interface AIGraphSummary {
  readonly nodes: readonly AINodeSummary[];
  readonly edges: readonly AIEdgeSummary[];
  readonly warnings?: readonly string[];
}

export interface AIProjectionContext {
  readonly scenarioId: string;
  readonly scenarioName: string;
  readonly status: 'known' | 'unknown' | 'partial' | 'not_applicable' | 'invalid';
  readonly outputs: Readonly<Record<string, unknown>>;
  readonly deltas: Readonly<Record<string, unknown>>;
  readonly warnings: readonly string[];
}

export interface AIContextBudget {
  readonly maxBytes: number;
}

export interface BuiltAIContext {
  readonly context: Readonly<Record<string, unknown>>;
  readonly sourceArtifacts: readonly AIEvidenceReference[];
  readonly byteLength: number;
  readonly reduced: boolean;
  readonly omissions: readonly string[];
}

const PROHIBITED_KEYS = new Set([
  'authorization',
  'cookie',
  'password',
  'passwordhash',
  'session',
  'sessionid',
  'sessiontoken',
  'sharetoken',
  'recovery',
  'recoverymetadata',
  'audit',
  'auditlog',
  'tracking',
  'trackingevents',
  'r2key',
  'storagekey',
  'ownerid',
  'userid',
  'responsible',
  'internalnotes',
  'email',
  'phone',
  'url',
  'nodeurl',
]);

function normalizedKey(key: string): string {
  return key.replace(/[^a-z0-9]/gi, '').toLowerCase();
}

export function redactAIContext(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(redactAIContext);
  if (!value || typeof value !== 'object') return value;
  const output: Record<string, unknown> = {};
  for (const [key, nested] of Object.entries(value)) {
    if (!PROHIBITED_KEYS.has(normalizedKey(key))) output[key] = redactAIContext(nested);
  }
  return output;
}

function byteLength(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).byteLength;
}

function prioritizeById<T extends { readonly id: string }>(
  values: readonly T[],
  selectedIds: ReadonlySet<string>,
): T[] {
  return [...values].sort((left, right) => {
    const leftSelected = selectedIds.has(left.id) ? 1 : 0;
    const rightSelected = selectedIds.has(right.id) ? 1 : 0;
    return rightSelected - leftSelected || left.id.localeCompare(right.id);
  });
}

function fitBudget(
  input: Record<string, unknown>,
  budget: AIContextBudget,
  selectedIds: ReadonlySet<string>,
): { context: Record<string, unknown>; reduced: boolean; omissions: string[] } {
  if (!Number.isInteger(budget.maxBytes) || budget.maxBytes <= 0) {
    throw new Error('AI context budget must be a positive integer.');
  }
  const context = redactAIContext(input) as Record<string, unknown>;
  if (byteLength(context) <= budget.maxBytes) return { context, reduced: false, omissions: [] };

  const omissions: string[] = [];
  const graph = context.graph;
  if (graph && typeof graph === 'object' && !Array.isArray(graph)) {
    const graphRecord = graph as Record<string, unknown>;
    if (Array.isArray(graphRecord.nodes)) {
      const nodes = prioritizeById(
        graphRecord.nodes.filter((node): node is AINodeSummary =>
          Boolean(node && typeof node === 'object' && typeof Reflect.get(node, 'id') === 'string'),
        ),
        selectedIds,
      );
      const selected = nodes.filter((node) => selectedIds.has(node.id));
      const remainder = nodes.filter((node) => !selectedIds.has(node.id));
      const retainedNodes = [...selected, ...remainder.slice(0, Math.max(0, 20 - selected.length))];
      graphRecord.nodes = retainedNodes;
      if (retainedNodes.length < nodes.length) omissions.push('graph.nodes');
    }
    if (Array.isArray(graphRecord.edges) && graphRecord.edges.length > 20) {
      graphRecord.edges = graphRecord.edges.slice(0, 20);
      omissions.push('graph.edges');
    }
    if (Array.isArray(graphRecord.warnings) && graphRecord.warnings.length > 10) {
      graphRecord.warnings = graphRecord.warnings.slice(0, 10);
      omissions.push('graph.warnings');
    }
  }

  if (byteLength(context) > budget.maxBytes && 'graph' in context) {
    delete context.graph;
    omissions.push('graph');
  }
  if (byteLength(context) > budget.maxBytes) {
    throw new Error('AI_CONTEXT_TOO_LARGE');
  }
  context.contextReduction = { reduced: true, omissions };
  if (byteLength(context) > budget.maxBytes) throw new Error('AI_CONTEXT_TOO_LARGE');
  return { context, reduced: true, omissions };
}

function build(
  input: Record<string, unknown>,
  sourceArtifacts: readonly AIEvidenceReference[],
  budget: AIContextBudget,
  selectedIds: readonly string[] = [],
): BuiltAIContext {
  const result = fitBudget(input, budget, new Set(selectedIds));
  return {
    context: result.context,
    sourceArtifacts,
    byteLength: byteLength(result.context),
    reduced: result.reduced,
    omissions: result.omissions,
  };
}

export function buildFunnelExplanationContext(input: {
  funnel: AIFunnelMetadata;
  graph: AIGraphSummary;
  scores?: ScoreResult;
  diagnosisSummary?: DiagnosisReport['evidenceSummary'];
  selectedNodeIds?: readonly string[];
  budget: AIContextBudget;
}): BuiltAIContext {
  return build(
    {
      funnel: input.funnel,
      graph: input.graph,
      scores: input.scores,
      diagnosisSummary: input.diagnosisSummary,
    },
    [{ artifactType: 'graph', artifactId: input.funnel.id }],
    input.budget,
    input.selectedNodeIds,
  );
}

export function buildDiagnosisExplanationContext(input: {
  funnelId: string;
  finding: DiagnosisFinding;
  budget: AIContextBudget;
}): BuiltAIContext {
  return build(
    { finding: input.finding },
    [
      { artifactType: 'diagnosis_finding', artifactId: input.finding.id },
      { artifactType: 'graph', artifactId: input.funnelId },
    ],
    input.budget,
    [...input.finding.affectedNodeIds, ...input.finding.affectedEdgeIds],
  );
}

export function buildBottleneckExplanationContext(input: {
  funnelId: string;
  finding: DiagnosisFinding;
  budget: AIContextBudget;
}): BuiltAIContext {
  if (input.finding.type !== 'bottleneck') {
    throw new Error('AI_OPERATION_UNSUPPORTED');
  }
  return buildDiagnosisExplanationContext(input);
}

export function buildImprovementContext(input: {
  funnelId: string;
  selectedNodeIds: readonly string[];
  scores: ScoreResult;
  priorities: DiagnosisReport['actionPlan'];
  metrics: Readonly<Record<string, unknown>>;
  budget: AIContextBudget;
}): BuiltAIContext {
  return build(
    {
      selectedNodeIds: input.selectedNodeIds,
      scores: input.scores,
      priorities: input.priorities,
      metrics: input.metrics,
    },
    [{ artifactType: 'diagnosis_report', artifactId: input.funnelId }],
    input.budget,
    input.selectedNodeIds,
  );
}

export function buildProjectionExplanationContext(input: {
  funnelId: string;
  projection: AIProjectionContext;
  budget: AIContextBudget;
}): BuiltAIContext {
  return build(
    {
      projection: input.projection,
      instruction: 'Interpret supplied outputs; do not recalculate.',
    },
    [{ artifactType: 'simulation_scenario', artifactId: input.projection.scenarioId }],
    input.budget,
  );
}

export function buildBenchmarkInterpretationContext(input: {
  funnelId: string;
  benchmark: BenchmarkResult;
  actualMetric: Readonly<Record<string, unknown>>;
  budget: AIContextBudget;
}): BuiltAIContext {
  return build(
    { benchmark: input.benchmark, actualMetric: input.actualMetric },
    [
      {
        artifactType: 'benchmark_result',
        artifactId: input.benchmark.matches[0]?.id ?? 'not_found',
      },
      { artifactType: 'graph', artifactId: input.funnelId },
    ],
    input.budget,
  );
}

export function toAIRequest(
  operation: AIRequest['operation'],
  modelClass: AIRequest['modelClass'],
  built: BuiltAIContext,
): AIRequest {
  return {
    operation,
    modelClass,
    context: built.context,
    sourceArtifacts: [...built.sourceArtifacts],
  };
}
